import {
    IHardwareHealthNotificationSubscriptionResponse,
    IHardwareNotificationSubscriptionResponse,
    IHardwareNotificationTriggerResponseChild,
    IIndicatorNotificationSubscriptionResponse,
    IIndicatorNotificationTriggerResponseChild,
    IMachineHealthNotificationSubscriptionResponse,
    MachineHealthEventType,
    NotificationHardwareType,
    NotificationType,
} from '@kcftech/prometheustypescriptapiclient';
import { NotificationSubscription } from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { TriggerSharedProps } from 'pages/Settings/NotificationSubscriptions/form/types';
import {
    hasEnabledNotificationMethods,
    machineHealthEventTypeName,
    NotificationEventCategory,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { memo } from 'react';
import styled from 'styled-components';
import { excludeNullAndUndefined, groupBy } from 'utils/array';
import { joinNodes } from 'utils/joinNodes';

const Label = styled.span`
    font-weight: 500;
    margin-right: 0.5rem;
`;

const PipeSeparator = styled.span`
    color: var(--icon-default);
    margin: 1rem;
`;

export const Pipe = () => <PipeSeparator>|</PipeSeparator>;

export const typeExists = (type: string | undefined) => !!type;

// Indicator Alerts Filters

export const typeWarningFilter = (trigger: IIndicatorNotificationTriggerResponseChild) =>
    trigger.ActivityEventCategory === NotificationEventCategory.Warning;

export const typeAlarmFilter = (trigger: IIndicatorNotificationTriggerResponseChild) =>
    trigger.ActivityEventCategory === NotificationEventCategory.Alarm;

export const typeCriticalAlarmAllFilter = (
    trigger: IIndicatorNotificationTriggerResponseChild
) => trigger.ActivityEventCategory === NotificationEventCategory.CriticalAlarmAll;

export const typeCriticalAlarmTemperatureFilter = (
    trigger: IIndicatorNotificationTriggerResponseChild
) => trigger.ActivityEventCategory === NotificationEventCategory.CriticalAlarmTemperature;

// Hardware Filters

export const typeBaseStationFilter = (
    trigger: IHardwareNotificationTriggerResponseChild
) => trigger.HardwareType === NotificationHardwareType.BaseStation;

export const typeRepeaterFilter = (trigger: IHardwareNotificationTriggerResponseChild) =>
    trigger.HardwareType === NotificationHardwareType.Repeater;

export const typeHubFilter = (trigger: IHardwareNotificationTriggerResponseChild) =>
    trigger.HardwareType === NotificationHardwareType.Hub;

/**
 *
 * @param children elements between which separators need to be added
 * @returns pipe separated elements
 */
export const insertSeparators = <T,>(children: (T | undefined)[], separator: T) =>
    children
        .filter((element) => element !== undefined)
        .reduce(
            (accum, currentElement, index, arr) =>
                arr.length === index + 1
                    ? [...accum, currentElement]
                    : [...accum, currentElement, separator],
            [] as (T | undefined)[]
        );

/**
 *
 * @param trigger Either an Indicator Notification Trigger or Hardware Notification Trigger
 * @returns The methods enabled for each trigger type
 */
export const getEnabledTypeList = (trigger: TriggerSharedProps) =>
    [
        trigger.IsEmailEnabled && 'Email',
        trigger.IsSmsEnabled && 'Text',
        trigger.IsSdConnectPushEnabled && 'Push',
    ]
        .filter((v) => !!v)
        .join(', ');

export type IndicatorAlertsNotificationTypeProps = Readonly<{
    subscription: IIndicatorNotificationSubscriptionResponse;
}>;

export const IndicatorAlertsNotificationProperties = memo(
    ({ subscription }: IndicatorAlertsNotificationTypeProps) => {
        const warnings = subscription.NotificationTriggers.filter(typeWarningFilter)
            .map(getEnabledTypeList)
            .filter(typeExists);

        const alarms = subscription.NotificationTriggers.filter(typeAlarmFilter)
            .map(getEnabledTypeList)
            .filter(typeExists);

        const criticalAlarmsAll = subscription.NotificationTriggers.filter(
            typeCriticalAlarmAllFilter
        )
            .map(getEnabledTypeList)
            .filter(typeExists);

        const criticalAlarmsTemperature = subscription.NotificationTriggers.filter(
            typeCriticalAlarmTemperatureFilter
        )
            .map(getEnabledTypeList)
            .filter(typeExists);

        const typesToRender = excludeNullAndUndefined([
            warnings.length > 0 ? (
                <>
                    <Label>Warnings: </Label>
                    {warnings}
                </>
            ) : undefined,
            alarms.length > 0 ? (
                <>
                    <Label>Alarms: </Label>
                    {alarms}
                </>
            ) : undefined,
            criticalAlarmsAll.length > 0 ? (
                <>
                    <Label>{`Critical Alarms (All): `}</Label>
                    {criticalAlarmsAll}
                </>
            ) : undefined,
            criticalAlarmsTemperature.length > 0 ? (
                <>
                    <Label>{`Critical Alarms (Temperature): `}</Label>
                    {criticalAlarmsTemperature}
                </>
            ) : undefined,
        ]);

        return <div>{joinNodes(typesToRender, <Pipe />)}</div>;
    }
);

export type HardwareNotificationTypeProps = Readonly<{
    subscription: IHardwareNotificationSubscriptionResponse;
}>;

export const HardwareNotificationProperties = memo(
    ({ subscription }: HardwareNotificationTypeProps) => {
        const baseStations = subscription.NotificationTriggers.filter(
            typeBaseStationFilter
        )
            .map(getEnabledTypeList)
            .filter(typeExists);

        const repeaters = subscription.NotificationTriggers.filter(typeRepeaterFilter)
            .map(getEnabledTypeList)
            .filter(typeExists);

        const hubs = subscription.NotificationTriggers.filter(typeHubFilter)
            .map(getEnabledTypeList)
            .filter(typeExists);

        const typesToRender = excludeNullAndUndefined([
            baseStations.length > 0 ? (
                <>
                    <Label>Base Stations: </Label>
                    {baseStations}
                </>
            ) : undefined,
            repeaters.length > 0 ? (
                <>
                    <Label>Repeaters: </Label>
                    {repeaters}
                </>
            ) : undefined,
            hubs.length > 0 ? (
                <>
                    <Label>Hubs: </Label>
                    {hubs}
                </>
            ) : undefined,
        ]);

        return <div>{joinNodes(typesToRender, <Pipe />)}</div>;
    }
);

export type MachineHealthNotificationTypeProps = Readonly<{
    subscription: IMachineHealthNotificationSubscriptionResponse;
}>;

export const MachineHealthNotificationProperties = memo(
    ({ subscription }: MachineHealthNotificationTypeProps) => {
        const groupedTriggers = groupBy(
            subscription.NotificationTriggers.filter(hasEnabledNotificationMethods),
            (trigger) => trigger.MachineHealthEventType
        );

        const typesToRender = excludeNullAndUndefined(
            Object.values(MachineHealthEventType).map((type) => {
                const triggers = groupedTriggers[type];
                if (!triggers || triggers.length === 0) {
                    return undefined;
                }
                return (
                    <>
                        <Label>{machineHealthEventTypeName[type]}: </Label>
                        {triggers.map(getEnabledTypeList)}
                    </>
                );
            })
        );

        return <div>{joinNodes(typesToRender, <Pipe />)}</div>;
    }
);

export type HardwareHealthNotificationTypeProps = Readonly<{
    subscription: IHardwareHealthNotificationSubscriptionResponse;
}>;

export const HardwareHealthNotificationProperties = memo(
    ({ subscription }: HardwareHealthNotificationTypeProps) => {
        const {
            faultTypes,
            notificationMethods,
        } = subscription.NotificationTriggers.reduce<{
            faultTypes: string[];
            notificationMethods: Set<string>;
        }>(
            (accum, trigger) => {
                accum.faultTypes.push(trigger.FaultType.FullName);

                if (trigger.IsEmailEnabled) {
                    accum.notificationMethods.add('Email');
                }
                if (trigger.IsSdConnectPushEnabled) {
                    accum.notificationMethods.add('Push');
                }
                return accum;
            },
            { faultTypes: [], notificationMethods: new Set() }
        );

        const faultTypesDisplay = faultTypes.join(', ');
        const notificationMethodsDisplay = [...notificationMethods].sort().join(', ');

        return (
            <div className="flex-column">
                <div className="flex-row">
                    <div className="bold mr-5 text-nowrap">Fault Type(s):</div>
                    <div
                        className="ellipsis-2-lines"
                        title={`Fault Types: ${faultTypesDisplay}`}
                    >
                        {faultTypesDisplay}
                    </div>
                </div>
                <div className="flex-row">
                    <div className="bold mr-5">Notification Method(s):</div>
                    <div>{notificationMethodsDisplay}</div>
                </div>
            </div>
        );
    }
);

export const NotificationPropertiesCell = ({
    rowData,
}: {
    rowData: NotificationSubscription;
}) => {
    switch (rowData._discriminator) {
        case NotificationType.Indicator:
            return <IndicatorAlertsNotificationProperties subscription={rowData} />;
        case NotificationType.Hardware:
            return <HardwareNotificationProperties subscription={rowData} />;
        case NotificationType.MachineHealth:
            return <MachineHealthNotificationProperties subscription={rowData} />;
        case NotificationType.HardwareHealth:
            return <HardwareHealthNotificationProperties subscription={rowData} />;
        default:
            throw new Error(
                // @ts-expect-error This is this case where we
                // failed to account for something, expecting
                // `never`. If this isn't a compile-time TS
                // error, you need to add an entry above.
                `NotificationProperties renderer not registered for notification type '${rowData._discriminator}'`
            );
    }
};
